import { LazyQueryResultTuple, QueryResult } from '@apollo/client';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box } from 'reflexbox';
import styled, { keyframes } from 'styled-components';

import { useAuthenticatedUser, useFeature, useViewer } from '@treams/treams-hooks';
import { MagicWandIcon, SendIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { PageType } from '@treams/treams-network';
import { AICard, Button, FlexGap, Input, Jumbo, Paragraph, tokens } from '@treams/treams-ui';

import GoalMoments from './GoalMoments';
import GoalsList, { GoalsQuery } from './GoalsList';
import GoalsTabs from './GoalsTabs';
import NewGoalButton from './NewGoalButton';

export function ViewerGoalsList(props: {
	selectedGoal?: graphql.Goal;
	onSelect?: (goal: graphql.Goal) => void;
	hideArchived?: boolean;
	header?: ReactNode;
	contained?: boolean;
}) {
	const viewer = useAuthenticatedUser();

	const query = graphql.useIndividualGoalsQuery({
		variables: { id: viewer.id, status: graphql.GoalStatus.Active, last: 10 },
		fetchPolicy: 'network-only',
	});
	const completedQuery = graphql.useIndividualGoalsQuery({
		variables: { id: viewer.id, status: graphql.GoalStatus.Completed, last: 10 },
		fetchPolicy: 'network-only',
	});

	const archivedQuery = graphql.useIndividualGoalsLazyQuery({
		variables: { id: viewer.id, status: graphql.GoalStatus.Archived, last: 10 },
		fetchPolicy: 'network-only',
	});

	return (
		<GoalsList
			header={props.header}
			contained={props.contained}
			moments={<GoalMoments userID={viewer.id} />}
			query={query as QueryResult<GoalsQuery>}
			path="user.goalsV2"
			completedQuery={completedQuery as QueryResult<GoalsQuery>}
			archivedQuery={
				!props.hideArchived
					? (archivedQuery as LazyQueryResultTuple<GoalsQuery, object>)
					: undefined
			}
			selectedGoal={props.selectedGoal}
			onSelect={props.onSelect}
			reorderable
		/>
	);
}

const fadeIn = keyframes`
	0% {
		opacity: 0;
		transform: translateY(-5px);
	}
	100% {
		opacity: 1;
	}
`;

const InputWrapper = styled(Box)`
	position: relative;
`;

const AnimatedPlaceholder = styled(Box)<{ visible: boolean }>`
	position: absolute;
	left: ${tokens.padding.control}px;
	top: 50%;
	transform: translateY(-50%);
	pointer-events: none;
	color: ${tokens.text.tertiary};
	font-size: ${tokens.typography.sizes.body}px;
	line-height: ${tokens.typography.lineHeights.body};
	animation: ${fadeIn} 0.3s ease-out;
	opacity: ${(props) => (props.visible ? 1 : 0)};
	transition: opacity 0.5s ease-out;
`;

function GoalAIShortcut() {
	const { t } = useTranslation();
	const [input, setInput] = useState('');
	const { push } = useHistory();

	const placeholders = t('goals.ai.shortcut.placeholders')
		.split(', ')
		.map((text) => text + '...');

	const [placeholderIdx, setPlaceholderIdx] = useState(0);
	const animatePlaceholder = input === '';

	const placeholder = placeholders[placeholderIdx];

	useEffect(() => {
		if (animatePlaceholder) {
			const interval = setInterval(() => {
				setPlaceholderIdx((idx) => (idx + 1) % placeholders.length);
			}, 4000);

			return () => clearInterval(interval);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [animatePlaceholder]);

	const startGoalAI = useCallback(() => {
		const prompt = input.trim();
		push(`/goals/new/?prompt=${encodeURIComponent(prompt)}`);
	}, [input, push]);

	return (
		<Box mb={`${tokens.gap.section}px`}>
			<AICard title={t('goals.ai.shortcut.trigger')} icon={<MagicWandIcon />}>
				<Paragraph mb={`${tokens.gap.list}px`}>{t('goals.ai.shortcut.title')}</Paragraph>
				<FlexGap
					alignItems={['stretch', 'stretch', 'center']}
					flexDirection={['column', 'column', 'row']}
					gap={tokens.gap.list}
				>
					<InputWrapper key={placeholderIdx} flexGrow={1}>
						<Input
							value={input}
							onChange={setInput}
							flexGrow={1}
							placeholder=""
							onKeyDown={(event) => {
								if (event.key === 'Enter') {
									startGoalAI();
									event.preventDefault();
								}
							}}
						/>
						{animatePlaceholder && (
							<AnimatedPlaceholder visible={animatePlaceholder}>{placeholder}</AnimatedPlaceholder>
						)}
					</InputWrapper>
					<Button
						title={t('common.start')}
						icon={<SendIcon />}
						type="ai"
						onClick={startGoalAI}
						flexShrink={0}
						width={['100%', '100%', 'auto']}
					/>
				</FlexGap>
			</AICard>
		</Box>
	);
}

export default function PersonalGoals() {
	const { t } = useTranslation();
	const viewer = useViewer();

	const hasAI = useFeature(graphql.FeatureName.Ai);

	return (
		<>
			<Jumbo.Editable
				page={PageType.GoalsPersonal}
				hasMediaUpload
				title={t('goals.index.title')}
				description={t('goals.index.description')}
				button={viewer?.id && <NewGoalButton userID={viewer.id} />}
				footer={<GoalsTabs />}
			/>
			<ViewerGoalsList header={hasAI ? <GoalAIShortcut /> : undefined} />
		</>
	);
}
